import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { Button, Modal, Table } from "globalComponents";
import { addNotification } from "store/slices/uiSlice";
import { notifTags } from "globalComponents/Notification";
import { errorHandler } from "globalResources/util";
import { getTheme } from "Theme";

const theme = getTheme();

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const CodeBox = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 0;
`;
const Code = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 2px;
`;
const Muted = styled.span`
  opacity: 0.7;
  font-size: 0.9em;
`;
const Sub = styled.div`
  font-weight: 400;
  opacity: 0.7;
`;
const Empty = styled.p`
  text-align: center;
  padding: 20px;
  opacity: 0.7;
`;
const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-weight: 600;
`;
const Select = styled.select`
  padding: 6px;
  border-radius: 5px;
  border: 2px solid ${theme.INPUT_BORDER};
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const STATUS_COLORS = {
  pending: "#b45309", // dark amber; theme.WARNING is too light on the striped rows
  accepted: theme.SUCCESS,
  declined: theme.BUTTON_COLOR_NEGATIVE,
  cancelled: theme.BUTTON_COLOR_GREY,
};
const fullName = (person) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ") || "Unknown";
const formatDate = (date) => (date ? moment(date).format("ll h:mm A") : "-");
const SORTERS = {
  name: (r) => fullName(r.person).toLowerCase(),
  createdAt: (r) => new Date(r.createdAt).getTime(),
  decidedAt: (r) => (r.decidedAt ? new Date(r.decidedAt).getTime() : 0),
  status: (r) => r.status,
};
const typeLabel = (t) =>
  t === "super" ? "Owner (super)" : t[0].toUpperCase() + t.slice(1);

// The join code and request tables. Shown on the Company page's "Join
// Requests" (tab="Pending") and "History" tabs, for manageJoinRequests.
const JoinRequests = ({ tab = "Pending" }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState({
    requests: [],
    groups: [],
    accountTypes: [],
    joinCode: "",
  });
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);
  const [sort, setSort] = useState({ sort: "createdAt", direction: -1 });
  const [acceptForm, setAcceptForm] = useState({
    accountType: "employee",
    groups: [],
  });

  const notify = (type, message) =>
    dispatch(addNotification({ type, message }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/join-requests", {
        params: { status: tab === "Pending" ? "pending" : "all" },
      });
      setData(res.data);
    } catch (err) {
      const { message } = await errorHandler(err);
      notify(notifTags.error, message || "Couldn't load join requests");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(data.joinCode);
      notify(notifTags.success, "Code copied");
    } catch {
      notify(notifTags.info, `Code: ${data.joinCode}`);
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Make a new code? The current code will stop working."))
      return;
    try {
      const res = await axios.post("/join-requests/code");
      setData((d) => ({ ...d, joinCode: res.data.joinCode }));
      notify(notifTags.success, "New code created");
    } catch (err) {
      const { message } = await errorHandler(err);
      notify(notifTags.error, message);
    }
  };

  const openAccept = (request) => {
    const defaultGroup = data.groups.find((g) => g.default);
    setAcceptForm({
      accountType: "employee",
      groups: defaultGroup ? [defaultGroup._id] : [],
    });
    setAccepting(request);
  };

  const accept = async () => {
    try {
      await axios.patch("/join-requests/accept", {
        id: accepting._id,
        ...acceptForm,
      });
      notify(
        notifTags.success,
        `${accepting.person?.firstName || "They"} can now log in to this company`,
      );
      setAccepting(null);
      load();
    } catch (err) {
      const { message } = await errorHandler(err);
      notify(notifTags.error, message || "Couldn't accept the request");
    }
  };

  const decline = async (request) => {
    if (
      !window.confirm(`Decline ${request.person?.firstName || "this"} request?`)
    )
      return;
    try {
      await axios.patch("/join-requests/decline", { id: request._id });
      notify(notifTags.success, "Request declined");
      load();
    } catch (err) {
      const { message } = await errorHandler(err);
      notify(notifTags.error, message);
    }
  };

  const toggleGroup = (id) =>
    setAcceptForm((f) => ({
      ...f,
      groups: f.groups.includes(id)
        ? f.groups.filter((g) => g !== id)
        : [...f.groups, id],
    }));

  const key = SORTERS[sort.sort] || SORTERS.createdAt;
  const rows = [...data.requests].sort((a, b) => {
    const x = key(a);
    const y = key(b);
    return (x > y ? 1 : x < y ? -1 : 0) * (sort.direction === 1 ? 1 : -1);
  });

  const personColumn = {
    label: "Person",
    width: "25%",
    sortBy: "name",
    render: (r) => (
      <div>
        <div>{fullName(r.person)}</div>
        <Sub>{r.person?.email || r.person?.phone}</Sub>
      </div>
    ),
  };
  const messageColumn = {
    label: "Message",
    width: "40%",
    render: (r) => <div>{r.message || "-"}</div>,
  };
  const requestedColumn = {
    label: "Requested",
    width: "20%",
    sortBy: "createdAt",
    render: (r) => <div>{formatDate(r.createdAt)}</div>,
  };
  const headers =
    tab === "Pending"
      ? [personColumn, messageColumn, requestedColumn]
      : [
          personColumn,
          { ...messageColumn, width: "30%" },
          requestedColumn,
          {
            label: "Status",
            width: "10%",
            sortBy: "status",
            render: (r) => (
              <div
                style={{
                  color: STATUS_COLORS[r.status],
                  textTransform: "capitalize",
                }}
              >
                {r.status}
              </div>
            ),
          },
          {
            label: "Decided",
            width: "20%",
            sortBy: "decidedAt",
            render: (r) => (
              <div>
                <div>{r.decidedAt ? formatDate(r.decidedAt) : "-"}</div>
                {r.decidedBy && <Sub>by {r.decidedBy}</Sub>}
              </div>
            ),
          },
        ];
  const isPending = (r) => r.status === "pending";
  const menu = [
    { label: "Accept", disabled: (r) => !isPending(r), action: openAccept },
    { label: "Decline", disabled: (r) => !isPending(r), action: decline },
  ];
  return (
    <Main>
      <div>
        <CodeBox>
          <Code data-testid="join-code">{data.joinCode || "--------"}</Code>
          <Button onClick={copyCode}>Copy</Button>
          <Button color={theme.BUTTON_COLOR_GREY} onClick={regenerate}>
            Replace Code
          </Button>
        </CodeBox>
        <Muted>
          Share this code with people who should join. They enter it when they
          sign up, and their request shows up below.
        </Muted>
        <div data-testid="join-requests-table">
          <Table
            loading={loading}
            data={rows}
            headers={headers}
            sortBy={sort.sort}
            sortDirection={sort.direction}
            onSort={(by, direction) => setSort({ sort: by, direction })}
            menu={tab === "Pending" ? menu : undefined}
            onClick={(r) => isPending(r) && openAccept(r)}
          />
          {!loading && rows.length === 0 && (
            <Empty data-testid="no-requests">
              {tab === "Pending" ? "No pending requests." : "No requests yet."}
            </Empty>
          )}
        </div>
      </div>
      {accepting && (
        <Modal onClose={() => setAccepting(null)} width="460px">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              width: "100%",
            }}
          >
            <h3 style={{ margin: 0 }}>
              Add{" "}
              {[accepting.person?.firstName, accepting.person?.lastName]
                .filter(Boolean)
                .join(" ")}
            </h3>
            <Field>
              Account type
              <Select
                name="accountType"
                value={acceptForm.accountType}
                onChange={(e) =>
                  setAcceptForm({ ...acceptForm, accountType: e.target.value })
                }
              >
                {data.accountTypes.map((t) => (
                  <option key={t} value={t}>
                    {typeLabel(t)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field as="div">
              Groups
              {data.groups.map((g) => (
                <label key={g._id} style={{ fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    name={`group-${g.name}`}
                    checked={acceptForm.groups.includes(g._id)}
                    onChange={() => toggleGroup(g._id)}
                  />{" "}
                  {g.name}
                  {g.default && <Muted> (default)</Muted>}
                </label>
              ))}
            </Field>
            <Actions>
              <Button
                color={theme.BUTTON_COLOR_GREY}
                onClick={() => setAccepting(null)}
              >
                Cancel
              </Button>
              <Button onClick={accept}>Accept</Button>
            </Actions>
          </div>
        </Modal>
      )}
    </Main>
  );
};

export default JoinRequests;
